import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Badge,
  Avatar,
  Divider,
  TextField,
  InputAdornment,
  Drawer,
  createTheme,
  ThemeProvider,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DoneIcon from "@mui/icons-material/Done";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import MenuIcon from "@mui/icons-material/Menu";

// --- Custom Theme (Your specified palette) ---
const warmTheme = createTheme({
  palette: {
    primary: {
      main: "#F4C542", // A warm, golden yellow
      contrastText: "#ffffff", // Dark text for readability on yellow
    },
    secondary: {
      main: "#FFF2A2", // A lighter yellow for accents
      contrastText: "#AB992E",
    },
    background: {
      default: "#F5F5F5", // A soft light gray for the main container
      paper: "#FFFFFF", // White for cards and surfaces
      chat: "#FAFAFA", // This line was missing, providing a light background for the chat bubbles
    },
    text: {
      primary: "#212121", // A dark gray for main text
      secondary: "#757575", // A medium gray for secondary text
    },
    divider: "#E0E0E0", // Light gray for dividers
  },
  typography: {
    fontFamily: "Roboto, sans-serif",
    h5: {
      fontWeight: 700,
    },
    body1: {
      fontSize: "0.95rem",
    },
    body2: {
      fontSize: "0.85rem",
    },
    caption: {
      fontSize: "0.75rem",
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "0 2px 10px 0 rgba(0,0,0,0.05)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "24px",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "24px",
            fieldset: {
              borderColor: "#E0E0E0",
            },
            "&:hover fieldset": {
              borderColor: "#E0E0E0", // Lighter yellow on hover
            },
            "&.Mui-focused fieldset": {
              borderColor: "#E0E0E0", // Golden yellow on focus
              borderWidth: "1px",
            },
          },
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          backgroundColor: "#FFC107",
          color: "#212121",
        },
      },
    },
  },
});

const BuyerChatPage = () => {
  const [conversations] = useState([
    {
      id: 1,
      seller: "Edwin Johnson",
      product: "1-Bedroom Apartment",
      lastMessage: "Cool! Is there a coffee machine...",
      unread: 0,
      time: "9:01",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg",
      online: true,
      lastMsgStatus: "seen",
    },
    {
      id: 2,
      seller: "Albert Flores",
      product: "Hi, I'm confirming your check-in...",
      lastMessage: "Hi, I'm confirming your check-in...",
      unread: 0,
      time: "10:37",
      avatar: "https://randomuser.me/api/portraits/men/33.jpg",
      online: false,
      lastMsgStatus: "delivered",
    },
    {
      id: 3,
      seller: "Annette Black",
      product: "I'm arriving tomorrow afternoon...",
      lastMessage: "I'm arriving tomorrow afternoon...",
      unread: 0,
      time: "9:15",
      avatar: "https://randomuser.me/api/portraits/women/34.jpg",
      online: true,
      lastMsgStatus: "sent",
    },
    {
      id: 4,
      seller: "Jerome Bell",
      product: "I've received your booking reque...",
      lastMessage: "I've received your booking reque...",
      unread: 0,
      time: "Thu",
      avatar: "https://randomuser.me/api/portraits/men/35.jpg",
      online: false,
      lastMsgStatus: "seen",
    },
    {
      id: 5,
      seller: "Darrell Steward",
      product: "Hello! Just a reminder that chec...",
      lastMessage: "Hello! Just a reminder that chec...",
      unread: 0,
      time: "Thu",
      avatar: "https://randomuser.me/api/portraits/men/36.jpg",
      online: true,
      lastMsgStatus: "seen",
    },
    {
      id: 6,
      seller: "Steven Jordan",
      product: "Sounds good! Could you confirm...",
      lastMessage: "Sounds good! Could you confirm...",
      unread: 2,
      time: "Wed",
      avatar: "https://randomuser.me/api/portraits/men/37.jpg",
      online: false,
      lastMsgStatus: "sent",
    },
    {
      id: 7,
      seller: "Wanda Hall",
      product: "Thanks for the update! Just to d...",
      lastMessage: "Thanks for the update! Just to d...",
      unread: 1,
      time: "Wed",
      avatar: "https://randomuser.me/api/portraits/women/38.jpg",
      online: true,
      lastMsgStatus: "delivered",
    },
    {
      id: 8,
      seller: "Victor Olson",
      product: "Hi, just letting you know that the...",
      lastMessage: "Hi, just letting you know that the...",
      unread: 0,
      time: "Tue",
      avatar: "https://randomuser.me/api/portraits/men/39.jpg",
      online: false,
      lastMsgStatus: "seen",
    },
  ]);

  const [activeChat, setActiveChat] = useState(conversations[0]);
  const [messages, setMessages] = useState([
    {
      sender: "buyer",
      text: "Hi, I saw your listing for scrap materials. Is it still available?",
      timestamp: "9:00",
      date: "Today",
      avatar: "https://randomuser.me/api/portraits/men/40.jpg",
    },
    {
      sender: "seller",
      text: "Yes, it is. I have newspapers, cardboard, and some metal pieces.",
      timestamp: "9:02",
      date: "Today",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg",
    },
    {
      sender: "buyer",
      text: "Great. Can you tell me the approximate weight of each?",
      timestamp: "9:03",
      date: "Today",
      avatar: "https://randomuser.me/api/portraits/men/40.jpg",
    },
    {
      sender: "seller",
      text: "Around 25kg newspapers, 12kg cardboard, and 8kg of mixed iron rods.",
      timestamp: "9:05",
      date: "Today",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg",
    },
    {
      sender: "buyer",
      text: "Alright. For newspapers we usually pay A$12/kg, cardboard A$8/kg, and iron A$22/kg. Does that work?",
      timestamp: "9:07",
      date: "Today",
      avatar: "https://randomuser.me/api/portraits/men/40.jpg",
    },
    {
      sender: "seller",
      text: "Can you make it A$25/kg for iron? The rates have gone up in my area.",
      timestamp: "9:09",
      date: "Today",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg",
    },
    {
      sender: "buyer",
      text: "I understand, but I can offer A$23/kg for iron. That’s the best I can do.",
      timestamp: "9:11",
      date: "Today",
      avatar: "https://randomuser.me/api/portraits/men/40.jpg",
    },
    {
      sender: "seller",
      text: "Okay, let’s go with A$23/kg then.",
      timestamp: "9:12",
      date: "Today",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg",
    },
    {
      sender: "buyer",
      text: "Perfect. Do you also have any plastic bottles or e-waste?",
      timestamp: "9:13",
      date: "Today",
      avatar: "https://randomuser.me/api/portraits/men/40.jpg",
    },
    {
      sender: "seller",
      text: "Yes, I’ve got around 6kg of PET bottles and an old CPU cabinet.",
      timestamp: "9:15",
      date: "Today",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg",
    },
    {
      sender: "buyer",
      text: "For bottles we pay A$10/kg. For the CPU, we’ll inspect and weigh it at pickup.",
      timestamp: "9:16",
      date: "Today",
      avatar: "https://randomuser.me/api/portraits/men/40.jpg",
    },
    {
      sender: "seller",
      text: "Sounds good. What’s the minimum total weight you require for pickup?",
      timestamp: "9:17",
      date: "Today",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg",
    },
    {
      sender: "buyer",
      text: "We do pickups above 20kg. From what you’ve listed, it’s well above that.",
      timestamp: "9:18",
      date: "Today",
      avatar: "https://randomuser.me/api/portraits/men/40.jpg",
    },
    {
      sender: "seller",
      text: "Alright. Can the pickup be scheduled tomorrow morning?",
      timestamp: "9:20",
      date: "Today",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg",
    },
    {
      sender: "buyer",
      text: "Yes, please share your location and preferred time slot.",
      timestamp: "9:21",
      date: "Today",
      avatar: "https://randomuser.me/api/portraits/men/40.jpg",
    },
    {
      sender: "seller",
      text: "I’m near City Market. Anytime between 10 and 11am works.",
      timestamp: "9:22",
      date: "Today",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg",
    },
    {
      sender: "buyer",
      text: "Got it. Our pickup agent will be there around 10:15am.",
      timestamp: "9:23",
      date: "Today",
      avatar: "https://randomuser.me/api/portraits/men/40.jpg",
    },
    {
      sender: "seller",
      text: "Thanks. How will I receive payment?",
      timestamp: "9:24",
      date: "Today",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg",
    },
    {
      sender: "buyer",
      text: "We can pay via UPI or cash on the spot, after weighing. Which do you prefer?",
      timestamp: "9:25",
      date: "Today",
      avatar: "https://randomuser.me/api/portraits/men/40.jpg",
    },
    {
      sender: "seller",
      text: "UPI works best for me. Thanks for arranging this.",
      timestamp: "9:26",
      date: "Today",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg",
    },
    {
      sender: "seller",
      text: "Really Appreciate it.",
      timestamp: "9:26",
      date: "Today",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg",
    },
  ]);

  const [newMessage, setNewMessage] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const filteredConversations = conversations.filter(
    (chat) =>
      chat.seller.toLowerCase().includes(searchTerm.toLowerCase()) ||
      chat.lastMessage.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const theme = useTheme();

  const handleSend = () => {
    if (!newMessage.trim()) return;
    setMessages((prev) => [
      ...prev,
      {
        sender: "buyer",
        text: newMessage,
        timestamp: "Just now",
        date: "Today",
        status: "sent",
        avatar: "https://randomuser.me/api/portraits/men/40.jpg",
      },
    ]);
    setNewMessage("");
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "sent":
        return (
          <AccessTimeIcon
            sx={{ fontSize: "0.8rem", color: theme.palette.text.secondary }}
          />
        );
      case "delivered":
        return (
          <DoneIcon
            sx={{ fontSize: "0.8rem", color: theme.palette.text.secondary }}
          />
        );
      case "seen":
        return (
          <DoneAllIcon
            sx={{ fontSize: "0.8rem", color: theme.palette.primary.main }}
          />
        );
      default:
        return null;
    }
  };

  const ChatBubble = ({ msg }) => {
    const theme = useTheme();
    const isBuyer = msg.sender === "buyer";
    const bubbleBg = isBuyer
      ? theme.palette.primary.main
      : theme.palette.background.paper;
    const bubbleColor = isBuyer
      ? theme.palette.primary.contrastText
      : theme.palette.text.primary;
    const avatarSrc = msg.avatar;

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: isBuyer ? "flex-end" : "flex-start",
          mb: 1.5,
          alignItems: "flex-end",
        }}
      >
        {!isBuyer && (
          <Avatar
            src={avatarSrc}
            sx={{ width: 32, height: 32, mr: 1, mb: 0.5 }}
          />
        )}
        <Paper
          sx={{
            p: 1.5,
            borderRadius: isBuyer ? "20px 20px 4px 20px" : "20px 20px 20px 4px",
            maxWidth: { xs: "85%", sm: "70%" },
            bgcolor: bubbleBg,
            color: bubbleColor,
            boxShadow: theme.shadows[1],
            position: "relative",
          }}
        >
          {/* Only text messages now */}
          <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
            {msg.text}
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              mt: 0.5,
              gap: 0.5,
              opacity: 0.8,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "inherit", fontSize: "0.7rem" }}
            >
              {msg.timestamp}
            </Typography>
            {isBuyer && getStatusIcon(msg.status)}
          </Box>
        </Paper>
        {isBuyer && (
          <Avatar
            src={avatarSrc}
            sx={{ width: 32, height: 32, ml: 1, mb: 0.5 }}
          />
        )}
      </Box>
    );
  };

  const groupedMessages = messages.reduce((acc, msg) => {
    if (!acc[msg.date]) acc[msg.date] = [];
    acc[msg.date].push(msg);
    return acc;
  }, {});

  const sidebar = (
    <Box
      sx={{
        width: 280,
        bgcolor: theme.palette.background.paper,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        boxShadow: 3,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          p: 2,
          fontWeight: "bold",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        Chats
      </Typography>
      <Box
        sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}
        className="scrollable-sidebar"
      >
        {conversations.map((chat) => (
          <Box
            key={chat.id}
            onClick={() => {
              setActiveChat(chat);
              setMobileOpen(false);
            }}
            tabIndex={0} // keeps it focusable for accessibility
            sx={{
              px: 2,
              py: 2,
              cursor: "pointer",
              mx: 2,
              my: 1,
              borderRadius: "12px",
              bgcolor:
                activeChat.id === chat.id
                  ? theme.palette.background.default
                  : "transparent",
              border:
                activeChat.id === chat.id
                  ? `1px solid ${theme.palette.divider}`
                  : "1px solid transparent",

              // 👇 this removes the default blue focus border
              outline: "none",
              "&:focus": {
                outline: "none",
                boxShadow: `0 0 0 2px ${theme.palette.divider}`, // gray focus ring
              },

              "&:hover": {
                bgcolor: theme.palette.background.default,
              },
              transition: "background-color 0.2s, border 0.2s, box-shadow 0.2s",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Badge
              color={chat.online ? "success" : "default"}
              variant="dot"
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
              <Avatar src={chat.avatar} sx={{ width: 48, height: 48 }} />
            </Badge>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.5,
                }}
              >
                <Typography noWrap variant="body1" sx={{ fontWeight: "bold" }}>
                  {chat.seller}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {chat.time}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  noWrap
                  variant="body2"
                  color="text.secondary"
                  sx={{ maxWidth: "calc(100% - 30px)" }}
                >
                  {chat.lastMessage}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {chat.unread > 0 && (
                    <Badge
                      badgeContent={chat.unread}
                      color="primary"
                      sx={{
                        ml: 1,
                        "& .MuiBadge-badge": {
                          fontSize: "0.7rem",
                          height: 18,
                          minWidth: 18,
                          fontWeight: "bold",
                        },
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <ThemeProvider theme={warmTheme}>
      <Box
        sx={{
          height: "90vh",
          display: "flex",
          bgcolor: theme.palette.background.default,
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.1)",
          mx: { xs: 1, sm: 2, md: 4 },
          my: { xs: 1, sm: 2 },
        }}
      >
        {/* Sidebar (Conversations) */}
        <Box
          sx={{
            width: { xs: "100%", md: "380px" },
            flexShrink: 0,
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            bgcolor: theme.palette.background.paper,
            borderRight: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}
          >
            <Typography variant="h5" sx={{ mb: 2 }}>
              Messages
            </Typography>
            {/* <TextField
              fullWidth
              variant="outlined"
              placeholder="Search..."
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: "24px",
                  bgcolor: theme.palette.background.default,
                  border: `1px solid ${theme.palette.divider}`,
                },
              }}
            /> */}
          </Box>
          {/* Search Box */}
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  backgroundColor: theme.palette.background.default,
                },
              }}
            />
          </Box>

          <Box
            sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}
            className="scrollable-sidebar"
          >
            {filteredConversations.map((chat) => (
              <Box
                key={chat.id}
                onClick={() => {
                  setActiveChat(chat);
                  setMobileOpen(false);
                }}
                tabIndex={0} // 👈 make it focusable
                sx={{
                  px: 2,
                  py: 2,
                  cursor: "pointer",
                  mx: 2,
                  my: 1,
                  borderRadius: "12px",
                  bgcolor:
                    activeChat.id === chat.id
                      ? theme.palette.divider
                      : "transparent",
                  // border:
                  //   activeChat.id === chat.id
                  //     ? `1px solid ${theme.palette.divider}` // gray border
                  //     : "1px solid transparent",

                  // 👇 Fix the blue outline
                  outline: "none",
                  "&:focus": {
                    outline: "none",
                    boxShadow: `0 0 0 2px ${theme.palette.divider}`, // gray focus ring
                  },

                  "&:hover": {
                    bgcolor: theme.palette.divider,
                  },
                  transition:
                    "background-color 0.2s, border 0.2s, box-shadow 0.2s",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Badge
                  color={chat.online ? "success" : "default"}
                  variant="dot"
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                >
                  <Avatar src={chat.avatar} sx={{ width: 48, height: 48 }} />
                </Badge>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      noWrap
                      variant="body1"
                      sx={{ fontWeight: "bold" }}
                    >
                      {chat.seller}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {chat.time}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      noWrap
                      variant="body2"
                      color="text.secondary"
                      sx={{ maxWidth: "calc(100% - 30px)" }}
                    >
                      {chat.lastMessage}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {chat.unread > 0 && (
                        <Badge
                          badgeContent={chat.unread}
                          color="primary"
                          sx={{
                            ml: 1,
                            "& .MuiBadge-badge": {
                              fontSize: "0.7rem",
                              height: 18,
                              minWidth: 18,
                              fontWeight: "bold",
                            },
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Sidebar Drawer (Mobile) */}
        <Drawer
          anchor="left"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: "block", md: "none" } }}
        >
          {sidebar}
        </Drawer>

        {/* Chat Area */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Header */}
          {activeChat && (
            <Box
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: `1px solid ${theme.palette.divider}`,
                bgcolor: theme.palette.background.paper,
                boxShadow: theme.shadows[1],
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <IconButton
                  sx={{ display: { xs: "inline-flex", md: "none" }, mr: 1 }}
                  onClick={() => setMobileOpen(true)}
                >
                  <MenuIcon />
                </IconButton>
                <Badge
                  color={activeChat.online ? "success" : "default"}
                  variant="dot"
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                >
                  <Avatar
                    src={activeChat.avatar}
                    sx={{ width: 48, height: 48 }}
                  />
                </Badge>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                    {activeChat.seller}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Online
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Messages */}
          <Box
            sx={{
              flex: 1,
              p: { xs: 1, sm: 2, md: 3 },
              overflowY: "auto",
              minHeight: 0,
              bgcolor: theme.palette.background.chat, // The corrected line
            }}
            className="scrollable-messages"
          >
            {Object.keys(groupedMessages).map((date) => (
              <Box key={date}>
                <Divider sx={{ my: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ bgcolor: theme.palette.background.chat, px: 1 }}
                  >
                    {date}
                  </Typography>
                </Divider>
                {groupedMessages[date].map((msg, idx) => (
                  <ChatBubble key={idx} msg={msg} />
                ))}
              </Box>
            ))}
          </Box>

          {/* Input */}
          <Box
            sx={{
              p: { xs: 1, sm: 2 },
              display: "flex",
              gap: 1.5,
              borderTop: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.paper,
              boxShadow: theme.shadows[2],
              alignItems: "center",
            }}
          >
            <TextField
              placeholder="Your message"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
              variant="outlined"
              size="small"
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  backgroundColor: theme.palette.background.default,
                  px: 2,
                  py: 1,
                  height: 50, // 👈 fixed height to match button
                },
              }}
            />
            <IconButton
              onClick={handleSend}
              sx={{
                bgcolor: "#FFB300",
                color: "#fff",
                // "&:hover": { bgcolor: "#FFB300", color: "#" },
                width: 50,
                height: 50, // 👈 same height as TextField
                borderRadius: "8px",
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Scrollbar CSS */}
        <style jsx global>{`
          .scrollable-sidebar::-webkit-scrollbar,
          .scrollable-messages::-webkit-scrollbar {
            width: 8px;
          }
          .scrollable-sidebar::-webkit-scrollbar-track,
          .scrollable-messages::-webkit-scrollbar-track {
            background: ${theme.palette.background.default};
            border-radius: 4px;
          }
          .scrollable-sidebar::-webkit-scrollbar-thumb,
          .scrollable-messages::-webkit-scrollbar-thumb {
            background: rgba(0, 0, 0, 0.2);
            border-radius: 4px;
            &:hover {
              background: rgba(0, 0, 0, 0.3);
            }
          }
          .scrollable-sidebar,
          .scrollable-messages {
            scrollbar-width: thin;
            scrollbar-color: rgba(0, 0, 0, 0.2)
              ${theme.palette.background.default};
          }
        `}</style>
      </Box>
    </ThemeProvider>
  );
};

export default BuyerChatPage;
